import { useState } from "react";
import { api, useAction, useApi } from "../../api.ts";
import {
  Badge, Button, Card, Dialog, EmptyState, ErrorText, Field, Icon, IconButton, Input, Loading, LoadError, Mono, PageHeader, Select, Switch,
  Table, TBody, Td, Textarea, Th, THead, Tabs, Tr, useToast,
} from "../../ui/index.tsx";
import { FormActions, PagedPanel, severityTone, UserCell, When } from "./_ops.tsx";
import type { CrashReport, ErrorCode, ErrorLog, LoginLog, Paged, Severity, WebError } from "../../../shared/types.ts";

type Tab = "crashes" | "errors" | "website" | "logins" | "codes";

export default function Diagnostics() {
  const [tab, setTab] = useState<Tab>("crashes");
  return (
    <>
      <PageHeader title="Diagnostics" description="Loader crashes and errors, website errors, sign-in history and the error code catalogue." />
      <Tabs<Tab> value={tab} onChange={setTab} tabs={[
        { id: "crashes", label: "Crashes" },
        { id: "errors", label: "Errors" },
        { id: "website", label: "Website" },
        { id: "logins", label: "Logins" },
        { id: "codes", label: "Error codes" },
      ]} />
      <div className="mt-6">
        {tab === "crashes" && (
          <PagedPanel<CrashReport> base="/admin/crashes" searchPlaceholder="Filter by username" emptyTitle="No crash reports" emptyIcon="bug"
            columns={["", "When", "User", "Code", "Game version", "OS", "HWID"]}
            renderRow={(c) => <CrashRow key={c.id} crash={c} />} />
        )}
        {tab === "errors" && (
          <PagedPanel<ErrorLog> base="/admin/errors" searchPlaceholder="Filter by username" emptyTitle="No errors logged" emptyIcon="alert"
            columns={["When", "User", "Code", "Message"]}
            renderRow={(e) => (
              <Tr key={e.id}>
                <Td><When at={e.created_at} /></Td>
                <Td><UserCell id={e.user_id} name={e.username} /></Td>
                <Td>{e.error_code ? <Badge tone="bad">{e.error_code}</Badge> : <span className="text-faint">—</span>}</Td>
                <Td className="max-w-lg break-words text-muted">{e.message || "—"}</Td>
              </Tr>
            )} />
        )}
        {tab === "logins" && (
          <PagedPanel<LoginLog> base="/admin/logins" searchPlaceholder="Filter by username" emptyTitle="No logins recorded" emptyIcon="users"
            columns={["When", "User", "Result", "Client", "IP", "Country", "HWID"]}
            renderRow={(l) => (
              <Tr key={l.id}>
                <Td><When at={l.created_at} /></Td>
                <Td>{l.username ? <UserCell id={l.user_id} name={l.username} /> : <span className="text-faint">unknown</span>}</Td>
                <Td><Badge tone={l.success ? "ok" : "bad"} dot>{l.success ? "Success" : "Failed"}</Badge></Td>
                <Td className="text-muted">{l.client ?? "—"}</Td>
                <Td><Mono>{l.ip ?? "—"}</Mono></Td>
                <Td className="text-muted">{l.country ?? "—"}</Td>
                <Td><Mono>{l.hwid ? `${l.hwid.slice(0, 12)}…` : "—"}</Mono></Td>
              </Tr>
            )} />
        )}
        {tab === "website" && <WebErrors />}
        {tab === "codes" && <ErrorCodes />}
      </div>
    </>
  );
}

/** Browser errors from exoware.app, one row per distinct error. "Mark fixed" deletes the group; it comes back if the bug does. */
function WebErrors() {
  const list = useApi<Paged<WebError>>("/admin/web-errors?limit=100");
  const toast = useToast();
  const resolve = async (e: WebError) => {
    try {
      await api.del(`/admin/web-errors/${e.id}`);
      list.setData((prev) => ({ ...prev!, items: prev!.items.filter((x) => x.id !== e.id), total: prev!.total - 1 }));
      toast("Marked fixed", "success");
    } catch (err) {
      toast(err instanceof Error ? err.message : "Update failed", "error");
    }
  };
  if (list.loading && !list.data) return <Loading />;
  if (list.error && !list.data) return <LoadError error={list.error} retry={list.reload} />;
  const items = list.data?.items ?? [];
  return (
    <Card title="Website errors" description="Errors thrown in visitors' browsers, grouped. New ones also ping the staff webhook." padded={false}
      actions={<Button size="sm" variant="ghost" icon="refresh" onClick={() => void list.reload()}>Refresh</Button>}>
      {items.length === 0 ? <EmptyState icon="check" title="No website errors" body="Nothing has gone wrong in anyone's browser lately." /> : (
        <Table>
          <THead><Tr><Th /><Th>Last seen</Th><Th>Error</Th><Th>Count</Th><Th>Page</Th><Th>User</Th><Th /></Tr></THead>
          <TBody>{items.map((e) => <WebErrorRow key={e.id} error={e} onResolve={() => resolve(e)} />)}</TBody>
        </Table>
      )}
    </Card>
  );
}

function WebErrorRow({ error: e, onResolve }: { error: WebError; onResolve: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tr onClick={() => setOpen(!open)}>
        <Td className="w-8 pr-0"><Icon name={open ? "chevronDown" : "chevronRight"} className="size-4 text-faint" /></Td>
        <Td><When at={e.last_seen} /></Td>
        <Td className="max-w-md">
          <div className="flex items-center gap-2">
            <Badge tone={e.kind === "render" ? "bad" : "warn"}>{e.kind}</Badge>
            <span className="truncate" title={e.message}>{e.message}</span>
          </div>
        </Td>
        <Td><Mono>{e.count.toLocaleString()}</Mono></Td>
        <Td><Mono className="text-muted">{e.path ?? "—"}</Mono></Td>
        <Td>{e.username ? <UserCell id={e.user_id} name={e.username} /> : <span className="text-faint">visitor</span>}</Td>
        <Td className="text-right"><Button size="sm" variant="ghost" icon="check" onClick={(ev) => { ev.stopPropagation(); onResolve(); }}>Mark fixed</Button></Td>
      </Tr>
      {open && (
        <tr className="bg-bg/60">
          <Td colSpan={7} className="space-y-3">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted">
              <span>First seen <When at={e.first_seen} /></span>
              {e.user_agent && <span className="break-all">Browser: <Mono className="text-fg">{e.user_agent}</Mono></span>}
            </div>
            {e.stack && <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-line bg-bg p-3 font-mono text-xs text-muted">{e.stack}</pre>}
          </Td>
        </tr>
      )}
    </>
  );
}

function CrashRow({ crash: c }: { crash: CrashReport }) {
  const [open, setOpen] = useState(false);
  const hasDetail = !!(c.stack_trace || c.driver_state);
  return (
    <>
      <Tr onClick={hasDetail ? () => setOpen(!open) : undefined}>
        <Td className="w-8 pr-0">{hasDetail && <Icon name={open ? "chevronDown" : "chevronRight"} className="size-4 text-faint" />}</Td>
        <Td><When at={c.created_at} /></Td>
        <Td><UserCell id={c.user_id} name={c.username} /></Td>
        <Td>{c.error_code ? <Badge tone="bad">{c.error_code}</Badge> : <span className="text-faint">—</span>}</Td>
        <Td className="text-muted">{c.game_version ?? "—"}</Td>
        <Td className="text-muted">{c.os_version ?? "—"}</Td>
        <Td><Mono>{c.hwid ? `${c.hwid.slice(0, 12)}…` : "—"}</Mono></Td>
      </Tr>
      {open && (
        <tr className="bg-bg/60">
          <Td colSpan={7} className="space-y-3">
            {c.driver_state && <div className="text-xs text-muted">Driver state: <Mono className="text-fg">{c.driver_state}</Mono></div>}
            {c.stack_trace && <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-line bg-bg p-3 font-mono text-xs text-muted">{c.stack_trace}</pre>}
          </Td>
        </tr>
      )}
    </>
  );
}

function ErrorCodes() {
  const codes = useApi<ErrorCode[]>("/admin/error-codes");
  const toast = useToast();
  const [editing, setEditing] = useState<ErrorCode | "new" | null>(null);

  const toggle = async (c: ErrorCode, active: boolean) => {
    try {
      await api.patch(`/admin/error-codes/${encodeURIComponent(c.code)}`, { active });
      codes.setData((prev) => (prev ?? []).map((x) => (x.code === c.code ? { ...x, active } : x)));
    } catch (e) {
      toast(e instanceof Error ? e.message : "Update failed", "error");
    }
  };

  return (
    <>
      <Card
        title="Error codes"
        description="Codes the loader can report, with the guidance users see."
        actions={<Button variant="primary" size="sm" icon="plus" onClick={() => setEditing("new")}>New code</Button>}
        padded={false}
      >
        {!codes.data ? (
          codes.loading ? <Loading /> : <LoadError error={codes.error} retry={() => void codes.reload()} />
        ) : codes.data.length === 0 ? (
          <EmptyState icon="bug" title="No error codes defined" />
        ) : (
          <Table>
            <THead><tr><Th>Code</Th><Th>Title</Th><Th>Severity</Th><Th>Resolution</Th><Th>Active</Th><Th /></tr></THead>
            <TBody>
              {codes.data.map((c) => (
                <Tr key={c.code} className={c.active ? undefined : "opacity-60"}>
                  <Td><Mono className="text-fg">{c.code}</Mono></Td>
                  <Td className="font-medium">{c.title}</Td>
                  <Td><Badge tone={severityTone(c.severity)} dot>{c.severity}</Badge></Td>
                  <Td className="max-w-sm truncate text-muted"><span title={c.resolution}>{c.resolution || "—"}</span></Td>
                  <Td><Switch checked={c.active} onChange={(v) => void toggle(c, v)} /></Td>
                  <Td className="text-right"><IconButton icon="edit" label="Edit" onClick={() => setEditing(c)} /></Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Dialog open={editing !== null} onClose={() => setEditing(null)} title={editing === "new" ? "New error code" : `Edit ${editing?.code ?? ""}`}>
        {editing !== null && (
          <CodeForm
            code={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); void codes.reload(); }}
          />
        )}
      </Dialog>
    </>
  );
}

function CodeForm({ code, onClose, onSaved }: { code: ErrorCode | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [id, setId] = useState(code?.code ?? "");
  const [title, setTitle] = useState(code?.title ?? "");
  const [description, setDescription] = useState(code?.description ?? "");
  const [severity, setSeverity] = useState<Severity>(code?.severity ?? "error");
  const [resolution, setResolution] = useState(code?.resolution ?? "");
  const [active, setActive] = useState(code?.active ?? true);

  const idOk = /^[A-Z0-9-]{3,32}$/.test(id);
  const save = useAction(async () => {
    const body = { title: title.trim(), description: description.trim(), severity, resolution: resolution.trim() };
    if (code) await api.patch(`/admin/error-codes/${encodeURIComponent(code.code)}`, { ...body, active });
    else await api.post("/admin/error-codes", { code: id, ...body });
    toast(code ? "Error code saved" : "Error code created", "success");
    onSaved();
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save.run(); }} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-[1fr_9rem]">
        <Field label="Code" hint={code ? undefined : "A-Z, 0-9 and hyphens, 3-32 characters"} error={!code && id && !idOk ? "Uppercase letters, digits and hyphens only (3-32)" : null}>
          <Input value={id} onChange={(e) => setId(e.target.value.toUpperCase())} className="font-mono" disabled={!!code} required />
        </Field>
        <Field label="Severity">
          <Select value={severity} onChange={(e) => setSeverity(e.target.value as Severity)}>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </Select>
        </Field>
      </div>
      <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} required /></Field>
      <Field label="Description"><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} /></Field>
      <Field label="Resolution" hint="Steps shown to the user"><Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={3} /></Field>
      {code && <Switch checked={active} onChange={setActive} label="Active" />}
      <ErrorText>{save.error}</ErrorText>
      <FormActions onCancel={onClose} submitLabel={code ? "Save changes" : "Create code"} loading={save.pending} disabled={!title.trim() || (!code && !idOk)} />
    </form>
  );
}
